import React from "react";
import { styled } from "@linaria/react";
import useInput from "@ui/lib/hooks/useInput";
import useToasts from "@ui/Toast/useToasts";
import ModalForm from "@ui/Modal/Form";
import { color, fonts } from "@ui/index";
import t from "../../../i18n";
import { usePatch } from "../../../api/useCall";
import { useUser } from "../../../api/useUser";
import InputV2 from "@ui/Input/InputV2";
import { useMutate } from "../../../api/useQuery";
import { isLeft } from "fp-ts/lib/Either";
import { short } from "diagnostics-lib/io";
import * as io from "io-ts";
import { useIndexedProjects } from "../../../api/useProjects";
import { getGlobalRoleName } from "./globalRoleOptions";
import { getProjectRoleName } from "./ProjectRoleMenu";

interface P {
  onRequestClose: () => void;
}

const Text = styled.div`
  font: ${fonts.body1};
  text-align: justify;
  margin-top: 12px;
  color: ${color.carbon800};
`;

const RolesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const RolesRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RolesLabel = styled.div`
  font: ${fonts.body1SemiBold};
  color: ${color.carbon800};
`;

const RolesValue = styled.div`
  font: ${fonts.body1};
  color: ${color.carbon900};
  overflow-wrap: anywhere;
`;

const RolesValueMuted = styled(RolesValue)`
  color: ${color.carbon500};
`;

const RolesDescription = styled(RolesValueMuted)`
  font-size: 12px;
  color: ${color.carbon800};
`;

const RolesValueStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RolesTable = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(120px, 160px);
  column-gap: 12px;
  row-gap: 6px;
`;

const RolesTableHeader = styled.div`
  font: ${fonts.body2SemiBold};
  color: ${color.carbon600};
`;

const RolesTableCell = styled.div`
  font: ${fonts.body1};
  color: ${color.carbon900};
  min-width: 0;
`;

const RoleProjectName = styled(RolesTableCell)`
  overflow-wrap: anywhere;
`;

const RoleLabel = styled(RolesTableCell)`
  overflow-wrap: anywhere;
`;

const ChangeUserNameModalForm = ({ onRequestClose }: P) => {
  const [submitted, setSubmitted] = React.useState(false);
  const user = useUser();
  const projects = useIndexedProjects();
  const updateUser = usePatch("/users/profile");
  const { addToast } = useToasts();
  const inputName = useInput(user.name ?? undefined);
  const mutate = useMutate("/users/profile", {});

  const errorInputUser = React.useMemo((): string | undefined => {
    if (inputName.value.trim() === "")
      return t("users.modal.create.input.name.error.cannot_be_empty");
    if (isLeft(short(io.string).decode(inputName.value)))
      return t("users.modal.create.input.name.error.text_to_long");
    return undefined;
  }, [inputName.value]);

  const detectSpaces = (name: string) => name.trim() !== name;

  const valid = errorInputUser === undefined;
  const dirty =
    submitted || detectSpaces(user?.name || "")
      ? inputName.dirty
      : user?.name !== inputName.value.trim();

  const handleSubmit = React.useCallback(async () => {
    setSubmitted(true);
    if (!valid) return false;
    const name = inputName.value.trim();

    const rollback = mutate({
      body: { ...user, name },
      status: 200,
      headers: undefined,
      streaming: false,
    });
    await updateUser({ body: { name } })
      .then(() => {
        addToast(
          "success",
          user.name
            ? t("users.modal.update.update_user.toast")
            : t("users.modal.create.user_name.toast"),
        );
      })
      .catch(() => rollback());

    return true;
  }, [addToast, inputName.value, mutate, updateUser, user, valid]);

  const globalRoleLabel = React.useMemo(() => getGlobalRoleName(user.roles.global), [user]);
  const globalRoleDescription = React.useMemo(() => {
    switch (user.roles.global) {
      case "SUPER_ADMIN":
        return t("users.table.row.menu.roles.application.super_admin.description");
      case "ADMIN":
        return t("users.table.row.menu.roles.application.admin.description");
      case "ALL_PROJECTS_READER":
        return t("users.table.row.menu.roles.application.read.description");
      case "ALL_PROJECTS_WRITER":
        return t("users.table.row.menu.roles.application.write.description");
      case "ALL_PROJECTS_SETUPER":
        return t("users.table.row.menu.roles.application.setup.description");
      case "PROD_READER":
        return t("users.table.row.menu.roles.application.read_prod_only.description");
      case "ALL_PROJECTS_READER_WITH_JUPYTER":
        return t("users.table.row.menu.roles.application.read_access_jupyterhub.description");
      default:
        return;
    }
  }, [user.roles.global]);
  const hasGlobalRole = user.roles.global !== null && user.roles.global !== "DIAGNOSER";
  const projectRoles = React.useMemo(
    () =>
      user.roles.projects
        .map(projectRole => {
          const project = projects[projectRole.projectId];
          const projectLabel = project?.name ?? projectRole.projectId;
          return {
            projectId: projectRole.projectId,
            projectLabel,
            roleLabel: getProjectRoleName(projectRole.role),
          };
        })
        .sort((a, b) => a.projectLabel.localeCompare(b.projectLabel)),
    [projects, user.roles.projects],
  );

  return (
    <ModalForm
      onRequestClose={onRequestClose}
      onSubmit={dirty && !errorInputUser ? handleSubmit : false}
      cancellable={Boolean(user.name)}
    >
      <InputV2
        autoFocus
        label={
          user.name
            ? t("users.modal.update.input.name.label")
            : t("users.modal.create.user_name.input.name.label")
        }
        placeholder={
          user.name
            ? t("users.modal.update.input.name")
            : t("users.modal.create.user_name.input.name")
        }
        error={inputName.pristine ? undefined : errorInputUser}
        {...inputName}
      />
      {!user.name && <Text>{t("users.modal.create.user_name.text")}</Text>}
      <RolesSection>
        <RolesRow>
          <RolesLabel>{t("users.modal.roles.identifier")}</RolesLabel>
          <RolesValue>{user.login}</RolesValue>
        </RolesRow>
        <RolesRow>
          <RolesLabel>{t("users.modal.roles.global_role")}</RolesLabel>
          {hasGlobalRole ? (
            <RolesValueStack>
              <RolesValue>{globalRoleLabel}</RolesValue>
              {globalRoleDescription && (
                <RolesDescription>{globalRoleDescription}</RolesDescription>
              )}
            </RolesValueStack>
          ) : (
            <RolesValueMuted>{t("users.table.row.menu.roles.no_role")}</RolesValueMuted>
          )}
        </RolesRow>
        <RolesRow>
          <RolesLabel>{t("users.modal.roles.specific_roles")}</RolesLabel>
          {projectRoles.length === 0 ? (
            <RolesValueMuted>{t("users.table.row.menu.roles.no_role")}</RolesValueMuted>
          ) : (
            <RolesTable>
              <RolesTableHeader>{t("users.modal.roles.table.project_name")}</RolesTableHeader>
              <RolesTableHeader>{t("users.modal.roles.table.role")}</RolesTableHeader>
              {projectRoles.map(projectRole => (
                <React.Fragment key={projectRole.projectId}>
                  <RoleProjectName>{projectRole.projectLabel}</RoleProjectName>
                  <RoleLabel>{projectRole.roleLabel}</RoleLabel>
                </React.Fragment>
              ))}
            </RolesTable>
          )}
        </RolesRow>
      </RolesSection>
    </ModalForm>
  );
};
export default ChangeUserNameModalForm;
